import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Button from '../../components/ui/Button';
import AuthenticatedNavigation from '../../components/navigation/AuthenticatedNavigation';
import NotificationCard from './components/NotificationCard';
import FilterPanel from './components/FilterPanel';
import EmptyState from './components/EmptyState';
import { useAuth } from '../../contexts/AuthContext';
import { notificationsService as supabaseNotifications } from '../../services/supabaseService';

const NotificationCenter = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [filters, setFilters] = useState({
    type: 'all',
    priority: 'all',
    status: 'all',
    search: ''
  });

  // Load notifications from Supabase
  useEffect(() => {
    const loadNotifications = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await supabaseNotifications?.getByUserId(user?.id);
        setNotifications(data);
      } catch (err) {
        setError(err?.message);
      } finally {
        setLoading(false);
      }
    };

    loadNotifications();
  }, [user]);

  const applyFilters = () => {
    let filtered = [...notifications];

    if (filters?.type !== 'all') {
      filtered = filtered?.filter(n => n?.type === filters?.type);
    }

    if (filters?.priority !== 'all') {
      filtered = filtered?.filter(n => n?.priority === filters?.priority);
    }

    if (filters?.status !== 'all') {
      filtered = filtered?.filter(n => 
        filters?.status === 'unread' ? !n?.isRead : n?.isRead
      );
    }

    if (filters?.search) {
      const searchLower = filters?.search?.toLowerCase();
      filtered = filtered?.filter(n => 
        n?.title?.toLowerCase()?.includes(searchLower) ||
        n?.message?.toLowerCase()?.includes(searchLower)
      );
    }

    setNotifications(filtered);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await supabaseNotifications?.markAsRead(notificationId);
      setNotifications((prev) =>
        prev?.map((notif) =>
          notif?.id === notificationId ? { ...notif, isRead: true } : notif
        )
      );
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user) return;

    try {
      await supabaseNotifications?.markAllAsRead(user?.id);
      setNotifications((prev) => prev?.map((notif) => ({ ...notif, isRead: true })));
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const handleDelete = (notificationId) => {
    // Implementation for deleting a notification
  };

  const handleBatchDelete = () => {
    if (selectedNotifications?.length === 0) return;
    // Implementation for deleting multiple notifications
  };

  const handleSelectNotification = (notificationId) => {
    setSelectedNotifications(prev => {
      if (prev?.includes(notificationId)) {
        return prev?.filter(id => id !== notificationId);
      }
      return [...prev, notificationId];
    });
  };

  const handleSelectAll = () => {
    if (selectedNotifications?.length === notifications?.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(notifications?.map(n => n?.id));
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification?.isRead) {
      handleMarkAsRead(notification?.id);
    }

    if (notification?.actionUrl) {
      navigate(notification?.actionUrl);
    }
  };

  const unreadCount = notifications?.filter(n => !n?.isRead)?.length || 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
        <AuthenticatedNavigation userRole="candidate" matchCount={0} />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center text-white">
            <p>Loading notifications...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
        <AuthenticatedNavigation userRole="candidate" matchCount={0} />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center text-red-400">
            <p>Error loading notifications: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F7FB] pb-20 lg:pb-0 lg:pl-60">
      <AuthenticatedNavigation 
        userRole={user?.userType} 
        matchCount={0}
      />

      <div className="pl-0 lg:pl-[280px] pb-20 lg:pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-[#111827] mb-2">
                  Notification Center
                </h1>
                <p className="text-sm md:text-base text-[#6B7280]">
                  Stay updated on matches, messages, and interview reminders
                </p>
              </div>
              <div className="flex items-center gap-3">
                {unreadCount > 0 && (
                  <Button
                    variant="outline"
                    size="default"
                    iconName="CheckCheck"
                    onClick={handleMarkAllAsRead}
                  >
                    Mark All Read
                  </Button>
                )}
                {selectedNotifications?.length > 0 && (
                  <Button
                    variant="danger"
                    size="default"
                    iconName="Trash2"
                    onClick={handleBatchDelete}
                  >
                    Delete ({selectedNotifications?.length})
                  </Button>
                )}
              </div>
            </div>

            <div className="mt-4 flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-sm bg-[#5B2DFF]"></div>
                <span className="text-[#6B7280]">
                  <span className="font-semibold text-[#111827]">{unreadCount}</span> Unread
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-sm bg-[#E5E7EB]"></div>
                <span className="text-[#6B7280]">
                  <span className="font-semibold text-[#111827]">{notifications?.length}</span> Total
                </span>
              </div>
            </div>
          </div>

          <FilterPanel filters={filters} onFilterChange={handleFilterChange} />

          {notifications?.length > 0 && (
            <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4 mb-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedNotifications?.length === notifications?.length && notifications?.length > 0}
                  onChange={handleSelectAll}
                  className="w-4 h-4 rounded border-[#E5E7EB] text-[#5B2DFF] focus:ring-[#5B2DFF]"
                />
                <span className="text-sm text-[#6B7280]">
                  {selectedNotifications?.length === notifications?.length && notifications?.length > 0
                    ? 'Deselect All' :'Select All'}
                </span>
              </label>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B2DFF]"></div>
            </div>
          ) : notifications?.length === 0 ? (
            <EmptyState 
              hasNotifications={notifications?.length > 0}
              filters={filters}
            />
          ) : (
            <div className="space-y-3">
              {notifications?.map((notification) => (
                <NotificationCard
                  key={notification?.id}
                  notification={notification}
                  isSelected={selectedNotifications?.includes(notification?.id)}
                  onSelect={handleSelectNotification}
                  onClick={handleNotificationClick}
                  onMarkAsRead={handleMarkAsRead}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationCenter;